import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import {
  createMedia,
  fetchAllMedia,
  getMedia,
  getMediaList,
  hideMedia,
  unhideMedia,
  updateMedia,
  updateMediaFile,
} from '../firestore/Media'
import { action, computed } from 'mobx'
import { Media } from '../models/Media'
import type { FirestoreMedia } from '../firestore/Media/schema'
import type { StaticModelCollection } from '../firestore-mobx/model'
import { MediaType } from '../types'
import { getDownloadURL, ref } from 'firebase/storage'
import { StorageFetchError } from '../firestore-mobx/errors'
import { SettingsDefaultProcessingAsset } from '../models/SettingsDefaultProcessingAsset'
import {
  getSettingsDefaultProcessingAsset,
  setSettingsDefaultProcessingAsset,
} from '../firestore/SettingsDefaultProcessingAsset'

type DataGetOptions =
  | { stream: true; fetch?: false }
  | { stream?: false; fetch: true }
  | { stream?: false; fetch?: false }

export class MediaCubit extends Cubit {
  repository: FirebaseRepository
  media: StaticModelCollection<Media>
  stream: boolean = false
  fetch: boolean = false
  mediasById: Map<string, Media> = new Map()
  defaultProcessingAsset: SettingsDefaultProcessingAsset

  constructor(repository: FirebaseRepository, options?: DataGetOptions) {
    super()
    this.repository = repository
    this.stream = options?.stream ?? false
    this.fetch = options?.fetch ?? false
    this.media = Media.emptyCollection(repository)
    this.defaultProcessingAsset =
      SettingsDefaultProcessingAsset.empty(repository)
  }

  async initialize(): Promise<void> {
    if (this.stream) {
      this.addStream(getMediaList(this.repository), (media) => {
        this.media.replaceModels(media)
      })

      // Stream the default processing asset setting
      this.addStream(
        getSettingsDefaultProcessingAsset(this.repository),
        (setting) => {
          this.defaultProcessingAsset.replaceModel(setting)
        }
      )
    }

    if (this.fetch) {
      this.fetchAllMedia().then((media) => {
        this.media.replaceModels(media)
      })
    }
  }

  fetchMedia(mediaId: string) {
    if (this.mediasById.has(mediaId)) {
      return this.mediasById.get(mediaId)!
    }

    const media = Media.empty(this.repository)
    this.mediasById.set(mediaId, media)

    this.addStream(getMedia(this.repository, { mediaId }), (m) => {
      media.replaceModel(m)
    })

    return media
  }

  @action
  async fetchAllMedia(): Promise<Media[]> {
    try {
      return await fetchAllMedia(this.repository)
    } catch (error) {
      console.error('Error fetching all media:', error)
      return []
    }
  }

  @action
  createMedia = (
    media: Pick<FirestoreMedia, 'mediaType' | 'mediaTitle' | 'mediaAltText'>,
    file: File
  ) => {
    return createMedia(this.repository, media, file)
  }

  getMedia = (mediaId: string) => {
    return getMedia(this.repository, { mediaId })
  }

  @action
  updateMedia = (
    mediaId: string,
    media: Pick<
      FirestoreMedia,
      'mediaTitle' | 'mediaAltText' | 'mediaVideoError' | 'mediaVideoDuration'
    >
  ) => {
    return updateMedia(this.repository, mediaId, media)
  }

  @action
  updateMediaFile = (mediaId: string, file: File) => {
    return updateMediaFile(this.repository, mediaId, file)
  }

  @action
  hideMedia = (mediaId: string) => {
    return hideMedia(this.repository, mediaId)
  }

  @action
  unhideMedia = (mediaId: string) => {
    return unhideMedia(this.repository, mediaId)
  }

  @action
  downloadMedia = async (media: Media) => {
    let url
    switch (media.data.mediaType) {
      case MediaType.Image:
        url = media.data.mediaImageURL
        break
      case MediaType.Pdf:
        url = media.data.mediaPdfURL
        break
      case MediaType.Video:
        url = media.data.mediaVideoURL
        break
      default:
        throw new Error('Invalid media type')
    }

    // Get a download url from firebase storage
    let downloadUrl: string
    try {
      downloadUrl = await getDownloadURL(ref(this.repository.storage, url))
    } catch (err) {
      throw new StorageFetchError(
        `Failed to get download URL for media: ${url}`,
        { cause: err }
      )
    }

    // Fetch the file
    let response: Response
    try {
      response = await fetch(downloadUrl)
    } catch (err) {
      throw new StorageFetchError(`Failed to fetch media file: ${url}`, {
        cause: err,
      })
    }
    const blob = await response.blob()

    // Create object URL and trigger download
    const objectUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = media.data.mediaTitle
    document.body.appendChild(a)
    a.click()

    // Clean up
    document.body.removeChild(a)
    window.URL.revokeObjectURL(objectUrl)
  }

  getMediaById(mediaId: string) {
    return this.media.models.find((m) => m.id === mediaId)
  }

  @computed
  get sortedMedia() {
    return this.media.models.sort((a, b) => {
      if (!a.data.updatedAt && !b.data.updatedAt) return 0
      // media with no updatedAt goes to the end
      if (!a.data.updatedAt) return 1
      if (!b.data.updatedAt) return -1
      return b.data.updatedAt.getTime() - a.data.updatedAt.getTime()
    })
  }

  @computed
  get imageMedia() {
    return this.sortedMedia.filter(
      ({ data }) =>
        !data.hidden &&
        data.mediaType === MediaType.Image &&
        data.mediaStorageURL &&
        data.mediaHasThumbnail
    )
  }

  @action
  setDefaultProcessingAsset = (mediaId: string) => {
    return setSettingsDefaultProcessingAsset(this.repository, mediaId)
  }

  @computed
  get defaultProcessingAssetId(): string | undefined {
    const mediaId = this.defaultProcessingAsset?.data?.mediaId
    return mediaId && mediaId !== '' ? mediaId : undefined
  }
}
